import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase, NewsArticle } from '@/utils/supabase';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '@/utils/seo';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import ElegantHeader from '@/components/feature/ElegantHeader';
import ElegantFooter from '@/components/feature/ElegantFooter';
import ElegantHero from '@/components/feature/ElegantHero';

/* ─── News Hero Background ─── */
const NEWS_HERO_BG = 'https://readdy.ai/api/search-image?query=Elegant%20abstract%20flowing%20golden%20light%20trails%20and%20particles%20on%20deep%20navy%20blue%20background%20minimal%20sophisticated%20high-end%20corporate%20aesthetic%20soft%20glowing%20bokeh%20orbs%20luxurious%20atmosphere%20no%20text&width=1920&height=800&seq=newshero1&orientation=landscape';

export default function NewsPage() {
  const { t, i18n } = useTranslation();

  const getSchemaLang = () => {
    const map: Record<string, string> = { ja: 'ja-JP', en: 'en-US', ko: 'ko-KR', 'zh-TW': 'zh-TW' };
    return map[i18n.language] || 'ja-JP';
  };
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [visibleRows, setVisibleRows] = useState<Set<number>>(new Set());
  const rowRefs = useRef<(HTMLElement | null)[]>([]);

  /* Section-level scroll reveal */
  const { ref: listRef } = useScrollReveal({ threshold: 0.05 });

  useSEO({
    title: t('seo.newsTitle'),
    description: t('seo.newsDescription'),
    keywords: t('seo.newsKeywords'),
    path: '/news',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(t('seo.newsTitle'), t('seo.newsDescription'), '/news', getSchemaLang()),
        generateBreadcrumbSchema([
          { name: t('nav.topPage'), path: '/' },
          { name: t('nav.news'), path: '/news' },
        ]),
      ],
    },
  });

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('9b01ede2_news_articles')
        .select('*, category:9b01ede2_news_categories(id, name, name_en, name_ko, name_zh, slug, created_at)')
        .eq('is_published', true)
        .order('published_at', { ascending: false });
      setArticles(data ?? []);
      setLoading(false);
    };
    fetchArticles();
  }, []);

  /* Per-row intersection observer for stagger */
  useEffect(() => {
    if (articles.length === 0) return;
    const observers: IntersectionObserver[] = [];
    rowRefs.current.forEach((el, i) => {
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleRows((prev) => new Set([...prev, i]));
            obs.disconnect();
          }
        },
        { threshold: 0.1 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [articles]);

  const formatDate = (d: string) => d.replace(/-/g, '.');

  return (
    <div className="min-h-screen" style={{ background: '#FAF9F6' }}>
      <ElegantHeader />

      <ElegantHero
        title={t('news.title')}
        subtitle={t('newsHeroSubtitle')}
        tag="News"
        backgroundImage={NEWS_HERO_BG}
        breadcrumb={[
          { label: t('nav.topPage'), to: '/' },
          { label: t('nav.news') },
        ]}
      />

      {/* Articles */}
      <main
        ref={listRef as React.RefObject<HTMLElement>}
        className="py-16 md:py-24 px-6 md:px-10 max-w-[1000px] mx-auto"
      >
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div
              className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: '#C9A227', borderTopColor: 'transparent' }}
            />
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20">
            <p
              className="text-sm"
              style={{
                color: '#A8A294',
                fontFamily: "'Noto Serif JP', serif",
              }}
            >
              {t('news.noArticles')}
            </p>
          </div>
        ) : (
          <div className="space-y-0">
            {articles.map((article, idx) => {
              const lang = i18n.language;
              const displayTitle =
                lang === 'en' && article.title_en
                  ? article.title_en
                  : lang === 'ko' && article.title_ko
                    ? article.title_ko
                    : (lang === 'zh' || lang === 'zhTW') && article.title_zh
                      ? article.title_zh
                      : article.title;
              const isRowVisible = visibleRows.has(idx);
              return (
                <Link
                  key={article.id}
                  to={`/news/${article.id}`}
                  ref={(el) => { rowRefs.current[idx] = el; }}
                  className="group block border-b py-6 transition-colors"
                  style={{
                    borderColor: 'rgba(212, 207, 196, 0.4)',
                    opacity: isRowVisible ? 1 : 0,
                    transform: isRowVisible ? 'translateX(0)' : 'translateX(-16px)',
                    transition: `opacity 0.5s ease ${Math.min(idx * 0.04, 0.4)}s, transform 0.5s ease ${Math.min(idx * 0.04, 0.4)}s, border-color 0.3s`,
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.borderBottomColor = 'rgba(201, 162, 39, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.borderBottomColor = 'rgba(212, 207, 196, 0.4)';
                  }}
                >
                  <div className="flex items-start gap-6">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-4 mb-3">
                        <time
                          className="text-xs tracking-wider whitespace-nowrap"
                          style={{
                            color: '#A8A294',
                            fontFamily: "'Inter', sans-serif",
                          }}
                        >
                          {formatDate(article.published_at)}
                        </time>
                        {article.category && (
                          <span
                            className="inline-block text-xs tracking-wider px-2.5 py-0.5 border font-medium"
                            style={{
                              background: 'rgba(201, 162, 39, 0.05)',
                              color: '#C9A227',
                              borderColor: 'rgba(201, 162, 39, 0.25)',
                              fontFamily: "'Inter', sans-serif",
                            }}
                          >
                            {lang === 'en' && article.category.name_en
                              ? article.category.name_en
                              : lang === 'ko' && article.category.name_ko
                                ? article.category.name_ko
                                : (lang === 'zh' || lang === 'zhTW') && article.category.name_zh
                                  ? article.category.name_zh
                                  : article.category.name}
                          </span>
                        )}
                      </div>
                      <h2
                        className="text-sm md:text-base leading-relaxed transition-colors duration-300 group-hover:text-gold-600"
                        style={{
                          color: '#3E3B34',
                          fontFamily: "'Noto Serif JP', serif",
                        }}
                      >
                        {displayTitle}
                      </h2>
                    </div>
                    <i
                      className="ri-arrow-right-line text-sm shrink-0 mt-1 transition-all duration-300 group-hover:translate-x-1 hidden sm:block"
                      style={{ color: '#D4CFC4' }}
                    />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>

      <ElegantFooter />
    </div>
  );
}
